import type { RegisterSession } from '../types';

export type RegisterCloseSummaryDenomRow = { denom: number; count: string };

export type RegisterCloseSummaryPrintInput = {
  session: RegisterSession;
  /** Staff member performing the close (signed-in user), shown on the slip. */
  closedByName?: string | null;
  closingFloatStr: string;
  closingCashStr: string;
  closingCardStr: string;
  cashDenoms: RegisterCloseSummaryDenomRow[];
  /** Sum of denom × count for finite, non-negative counts. */
  cashDenomTotal: number;
};

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatCurrency(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

/** Keep long IDs from wrapping awkwardly on 72mm paper. */
function truncateForReceipt(s: string, maxLen: number): string {
  const t = String(s);
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

/** Receipt row: label left, value right (monospace + narrow body). */
function receiptRow(label: string, valueHtml: string): string {
  return `<div class="row"><span class="k">${label}</span><span class="v">${valueHtml}</span></div>`;
}

function buildRegisterCloseSummaryHtml(input: RegisterCloseSummaryPrintInput): string {
  const { session, closedByName, closingFloatStr, closingCashStr, closingCardStr, cashDenoms, cashDenomTotal } =
    input;
  const opening = Number(session.openingFloat ?? 0);
  const closingFloatNum = Number(closingFloatStr);
  const cashSalesNum = Number(closingCashStr);
  /** Matches ReportsPage handleCloseSession variance passed to onForceCloseSession. */
  const varianceSubmitted =
    Number.isFinite(closingFloatNum) && Number.isFinite(cashSalesNum)
      ? closingFloatNum - (opening + cashSalesNum)
      : NaN;

  const denomRows = cashDenoms
    .map((d) => {
      const c = Number(d.count);
      const line = Number.isFinite(c) && c >= 0 ? d.denom * c : 0;
      const qty = escapeHtml(String(d.count));
      const denom = formatCurrency(d.denom);
      return receiptRow(`${denom} × ${qty}`, formatCurrency(line));
    })
    .join('');

  const diffClosingVsDenom =
    Number.isFinite(closingFloatNum) && Number.isFinite(cashDenomTotal)
      ? closingFloatNum - cashDenomTotal
      : NaN;

  const printedAt = escapeHtml(
    new Date().toLocaleString('en-NZ', { timeZone: 'Pacific/Auckland', hour12: false }),
  );

  const terminalLine = escapeHtml(
    truncateForReceipt(String(session.terminalName || session.terminalId || '—'), 36),
  );
  const sessionLine = escapeHtml(truncateForReceipt(session.id, 40));
  const cashierLine = escapeHtml(truncateForReceipt(session.cashierName, 40));
  const closedByLine = escapeHtml(
    truncateForReceipt(
      (closedByName && String(closedByName).trim()) || '—',
      40,
    ),
  );

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/><meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Register close</title>
<style>
  /* ~80mm thermal (Star / same receipt printer via browser print) */
  @page { size: 80mm auto; margin: 0; }
  *, *::before, *::after { box-sizing: border-box; }
  html { margin: 0; padding: 0; }
  body {
    margin: 0 auto;
    padding: 3mm 4mm 5mm;
    width: 72mm;
    max-width: 100%;
    font-family: ui-monospace, "Cascadia Mono", "Consolas", "Courier New", monospace;
    font-size: 9.5pt;
    line-height: 1.28;
    color: #000;
    background: #fff;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  h1 {
    font-size: 10.5pt;
    font-weight: 700;
    text-align: center;
    margin: 0 0 4px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  hr.rule {
    border: 0;
    border-top: 1px dashed #000;
    margin: 6px 0;
    opacity: 0.85;
  }
  .section {
    margin: 8px 0 4px;
    font-size: 8.5pt;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 6px;
    margin: 3px 0;
    width: 100%;
  }
  .row .k {
    flex: 1 1 auto;
    min-width: 0;
    word-break: break-word;
    padding-right: 4px;
  }
  .row .v {
    flex: 0 0 auto;
    text-align: right;
    white-space: nowrap;
    font-weight: 600;
  }
  .muted {
    margin-top: 8px;
    font-size: 8pt;
    color: #333;
    text-align: center;
  }
  @media print {
    body { padding: 2mm 3mm 4mm; }
  }
</style></head><body>
<h1>Register close</h1>
<hr class="rule"/>
${receiptRow('Closed by', closedByLine)}
${receiptRow('Terminal', terminalLine)}
${receiptRow('Session', sessionLine)}
${receiptRow('Session cashier', cashierLine)}
<hr class="rule"/>
<div class="section">Floats &amp; sales</div>
${receiptRow('Opening float', formatCurrency(opening))}
${receiptRow('Closing float', escapeHtml(closingFloatStr.trim() === '' ? '—' : closingFloatStr))}
${receiptRow('Cash sales', escapeHtml(closingCashStr.trim() === '' ? '—' : closingCashStr))}
${receiptRow('Card sales', escapeHtml(closingCardStr.trim() === '' ? '—' : closingCardStr))}
${receiptRow('Variance (submitted)', Number.isFinite(varianceSubmitted) ? formatCurrency(varianceSubmitted) : '—')}
<hr class="rule"/>
<div class="section">Cash denominations</div>
${denomRows || receiptRow('(none entered)', '—')}
${receiptRow('Denom total', formatCurrency(cashDenomTotal))}
${
  Number.isFinite(diffClosingVsDenom)
    ? receiptRow('Close − denom', formatCurrency(diffClosingVsDenom))
    : ''
}
<hr class="rule"/>
<p class="muted">Printed ${printedAt}</p>
</body></html>`;
}

/**
 * Opens a print dialog with register close summary (who closed, terminal, floats, variance, denominations).
 * HTML is sized for ~80mm thermal (Star / same receipt printer as daily receipts).
 */
export function printRegisterCloseSummary(input: RegisterCloseSummaryPrintInput): void {
  const html = buildRegisterCloseSummaryHtml(input);
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert('Pop-up blocked. Allow pop-ups for this site, then try Print summary again.');
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  const waitForImages = async () => {
    const images = Array.from(printWindow.document.images);
    if (images.length === 0) return;
    await Promise.all(
      images.map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise<void>((resolve) => {
          const done = () => resolve();
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }),
    );
  };

  const runPrint = () => {
    printWindow.focus();
    printWindow.print();
  };

  const closeLater = () => {
    try {
      if (!printWindow.closed) printWindow.close();
    } catch {
      /* ignore */
    }
  };

  printWindow.addEventListener('afterprint', closeLater, { once: true });
  window.setTimeout(closeLater, 120_000);

  if (printWindow.document.readyState === 'complete') {
    void waitForImages().then(() => window.setTimeout(runPrint, 100));
  } else {
    printWindow.addEventListener(
      'load',
      () => {
        void waitForImages().then(() => window.setTimeout(runPrint, 100));
      },
      { once: true },
    );
  }
}
